import { ItemModel } from 'models';
import { Stack, NumberInput, InputGroup, InputLeftElement, FormControl, FormLabel, Button, FormErrorMessage, NumberInputField } from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import BasicItemCard from '../../Item/BasicItemCard/BasicItemCard';
import DurationDropdown from '../../DurationDropdown/DurationDropdown';

export interface ItemAskFormData {
	askPrice?: number;
	askExpiration?: number;
}

interface ItemAskFormProps {
	item: ItemModel;
	onSubmit?: (data: ItemAskFormData) => void;
	onChange?: (data: ItemAskFormData) => void;
	defaultFormData?: ItemAskFormData;
	durationOptions?: number[];
	isDisabled?: boolean;
	showSubmit?: boolean;
	showItemCard?: boolean;
	showExpirationDropdown?: boolean;
}

export default function ItemAskForm({
	item,
	onSubmit,
	onChange,
	defaultFormData = { askExpiration: 30 },
	durationOptions = [30, 60, 90],
	isDisabled = false,
	showSubmit = true,
	showItemCard = true,
	showExpirationDropdown = false
}: ItemAskFormProps) {
	const { register, handleSubmit, control, getValues, setValue, formState: { errors } } = useForm<ItemAskFormData>({
		defaultValues: defaultFormData,
	});

	const onFormSubmit = (data: ItemAskFormData) => {
		onSubmit?.(data);
	};

	const handleFormChange = () => {
		if (onChange) {
			onChange(getValues());
		}
	};

	return (
		<Stack gap={4} width="100%">
			{showItemCard && <BasicItemCard item={item} />}
			<form onSubmit={handleSubmit(onFormSubmit)} onChange={handleFormChange}>
				<Stack spacing={8}>
					<FormControl isDisabled={isDisabled} isInvalid={!!errors.askPrice}>
						<FormLabel htmlFor="askPrice" fontSize={"sm"}>Ask Price</FormLabel>
						<InputGroup>
							<InputLeftElement
								pointerEvents='none'
								color='gray.500'
								children='$'
							/>
							<NumberInput width={'100%'}>
								<NumberInputField
									{...register('askPrice', {
										valueAsNumber: true,
										validate: {
											isNumber: (v) => v && !isNaN(v) || 'Ask Price must be a valid number',
											isPositive: v => (v && v > 0) || 'Ask Price must be greater than 0',
										}
									})}
									paddingLeft={10}
									fontSize="sm"
									placeholder='Enter Ask Amount (USD)'
								/>
							</NumberInput>
						</InputGroup>
						<FormErrorMessage>{errors.askPrice && errors.askPrice.message}</FormErrorMessage>
					</FormControl>

					{showExpirationDropdown && (
						<FormControl isDisabled={isDisabled} isInvalid={!!errors.askExpiration}>
							<FormLabel htmlFor="askExpiration" fontSize={"sm"}>Ask Expiration</FormLabel>
							<Controller
								control={control}
								name="askExpiration"
								rules={{ required: 'Ask expiration is required' }}
								render={({ field: { value } }) => (
									<DurationDropdown
										durations={durationOptions}
										onSelect={(selectedValue) => {
											setValue('askExpiration', selectedValue, { shouldDirty: true });
											handleFormChange();
										}}
										selectedDuration={value}
										disabled={isDisabled}
									/>
								)}
							/>
							<FormErrorMessage>{errors.askExpiration && errors.askExpiration.message}</FormErrorMessage>
						</FormControl>
					)}

					{!isDisabled && showSubmit && (
						<Button mt={4} colorScheme="teal" type="submit">
							Place Ask
						</Button>
					)}
				</Stack>
			</form>
		</Stack >
	);
}
